import { Maybe } from 'graphql/jsutils/Maybe';

import { getNodes } from '@/utils/graphqlUtils';

import {
  EntityMap_BeneficiaryConnectionFragment,
  EntityMap_ClientProfileFragment,
} from '../graphql/EntityMap.generated';
import {
  EntityGraph,
  EntityGraphEdgeAttributes,
  EntityGraphNodeAttributes,
} from '../types';
import { getBeneficiaryEdgeLabelValue } from '../utils/beneficiaries';
import { createEdge, EdgeInput } from '../utils/edges';
import { $getSupportedEntityNodeTypename } from '../utils/nodes';
import { entitySubtypeToNodeCategorization } from './constants';
import {
  entitySubtypeToTile,
  individualBeneficiaryTile,
  organizationBeneficiaryTile,
} from './tiles';

export function createBeneficiaryEdge(
  graph: EntityGraph,
  b: EntityMap_BeneficiaryConnectionFragment,
  edge: EdgeInput
) {
  const accessParameters = getNodes(b.accessParameters) ?? [];

  // Helper to check if edgeLabel is EdgeLabelProps (not a ReactNode)
  const isEdgeLabelProps = (
    label: unknown
  ): label is { variant?: string; label?: string; value?: string } => {
    return (
      typeof label === 'object' &&
      label !== null &&
      !('$$typeof' in label) && // Exclude React elements
      ('variant' in label || 'label' in label || 'value' in label)
    );
  };

  return createEdge(graph, {
    ...edge,
    data: {
      ...edge.data,
      edgeLabel: {
        variant: 'primary',
        label:
          accessParameters.length > 1 ? 'Multiple distributions' : undefined,
        // value will evaluate to empty string if there are none, or 2+ distributions
        value: getBeneficiaryEdgeLabelValue(b),
        ...(edge.data && isEdgeLabelProps(edge.data.edgeLabel)
          ? edge.data.edgeLabel
          : {}),
      },
    },
  });
}

export function createBeneficiaryNodeFromClientProfile(
  _graph: EntityGraph,
  individual: EntityMap_ClientProfileFragment
): EntityGraphNodeAttributes {
  const node = individualBeneficiaryTile(individual);
  // Guarantee the discriminator so node-kind switches (the design-mode hover
  // toolbar, the summary panel) recognize this as an editable individual even
  // if the profile subselection omitted `__typename`.
  return {
    node,
    data: { ...individual, __typename: 'ClientProfile' },
    categorizationType: 'INDIVIDUAL',
  };
}

export function createBeneficiaryNode(
  graph: EntityGraph,
  b?: EntityMap_BeneficiaryConnectionFragment | null
): EntityGraphNodeAttributes | null {
  if (!b) return null;

  const {
    individualBeneficiariesById,
    organizationBeneficiariesById,
    entitiesById,
  } = graph.getAttribute('byId');

  const individual = individualBeneficiariesById[b.individual?.id ?? ''];
  if (individual) {
    return createBeneficiaryNodeFromClientProfile(graph, individual);
  }

  const org = organizationBeneficiariesById[b.organization?.id ?? ''];
  if (org) {
    return {
      node: organizationBeneficiaryTile(org),
      // Guarantee the discriminator so the design-mode tile recognizes an
      // editable organization even if the payload omitted `__typename`.
      data: { ...org, __typename: 'ClientOrganization' },
      categorizationType: 'INDIVIDUAL',
    };
  }

  const entity = entitiesById[b.entity?.id ?? ''];
  if (entity) {
    const supporteEntityTypename = $getSupportedEntityNodeTypename(
      entity.subtype.__typename!
    );
    const node = entitySubtypeToTile[supporteEntityTypename](entity);
    return {
      node,
      // Guarantee the discriminator so the design-mode tile recognizes an
      // editable entity even if the payload omitted `__typename`.
      data: { ...entity, __typename: 'Entity' },
      categorizationType:
        entitySubtypeToNodeCategorization[supporteEntityTypename],
    };
  }

  return null;
}

export function addBeneficiaries(
  graph: EntityGraph,
  source: string,
  beneficiaries: Maybe<Maybe<EntityMap_BeneficiaryConnectionFragment>[]> = [],
  edgeInput: Omit<EdgeInput, 'source' | 'target'> = {},
  edgeAttributes: Partial<EntityGraphEdgeAttributes> = {}
) {
  beneficiaries?.forEach((b) => {
    const node = createBeneficiaryNode(graph, b);
    if (!node || !b) return;

    graph.addNodeSafe(node.node.id, node);
    const target = node.node.id;
    const edge = createBeneficiaryEdge(graph, b, {
      source,
      target,
      ...edgeInput,
    });
    graph.addEdgeSafe(source, target, {
      edge,
      type: 'beneficiary',
      ...edgeAttributes,
    });
  });
}
